/**
 * Header Component - Componente unificado de cabeçalho
 * 
 * Características:
 * - Responsivo usando Safe Area Insets
 * - Escala dinâmica para diferentes tamanhos de tela
 * - Memoizado para performance
 * - Suporta múltiplos variantes (home, community, default)
 */

import React, { memo, useMemo } from 'react';
import {
    View,
    TouchableOpacity,
    StyleSheet,
    Platform,
} from 'react-native';
import { Text } from '../ui/AppText';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { moderateScale, scaleWidth, fontSizes, spacing, isSmallScreen } from '../../utils/responsive';

// Cores fixas (estilo dark mode consistente)
const HEADER_COLORS = {
    bgPrimary: '#0B0D17',
    bgSecondary: '#151821',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255,255,255,0.7)',
    accentPrimary: '#5B8FF9',
    accentGold: '#FFD700',
    accentPurple: '#8B5CF6',
    border: '#2A2D3A',
};

/**
 * Header Principal
 * 
 * @param {Object} props
 * @param {string} props.title - Título principal (ex: "Olá, Estudante")
 * @param {string} props.subtitle - Subtítulo opcional
 * @param {boolean} props.showBackButton - Mostrar botão voltar
 * @param {boolean} props.showStats - Mostrar badge de estatísticas
 * @param {boolean} props.showUserSection - Mostrar seção de usuário à direita
 * @param {Function} props.onBackPress - Callback do botão voltar
 * @param {Function} props.onUserPress - Callback da área de usuário
 * @param {Object} props.stats - Estatísticas para exibir { questionCount, categoryCount }
 * @param {Object} props.userStats - Stats do usuário { rank, xp }
 * @param {'home' | 'community' | 'default'} props.variant - Variante visual
 * @param {React.ReactNode} props.rightContent - Conteúdo customizado à direita
 * @param {React.ReactNode} props.centerContent - Conteúdo customizado no centro
 * @param {Function} props.onBadgePress - Callback opcional para tornar o badge acionável
 * @param {string[]} props.gradient - Array de cores p/ fundo em gradiente (aplicado SÓ no tema escuro; no tema claro mantém o fundo sólido)
 */
const Header = memo(({
    title,
    subtitle,
    showBackButton = false,
    showStats = false,
    showUserSection = true,
    onBackPress,
    onUserPress,
    stats = {},
    userStats = {},
    variant = 'default',
    rightContent,
    centerContent,
    badge,
    onBadgePress,
    gradient,
    matchTabBar = false,
}) => {
    const insets = useSafeAreaInsets();
    const { theme, isDarkMode } = useTheme();
    const { isAuthenticated, profile, loading: authLoading } = useAuth();

    // Gradiente só no tema escuro (no claro mantém o fundo sólido do tema).
    const useGradient = !!gradient && isDarkMode;

    // Cores baseadas no tema
    const colors = useMemo(() => {
        if (isDarkMode) {
            // Modo escuro - mantido intacto
            return {
                background: HEADER_COLORS.bgPrimary,
                text: HEADER_COLORS.textPrimary,
                textSecondary: HEADER_COLORS.textSecondary,
                accent: HEADER_COLORS.accentPrimary,
                border: HEADER_COLORS.border,
                statsBackground: 'rgba(255,255,255,0.12)',
                statsText: '#FFFFFF',
                statsLabel: 'rgba(255,255,255,0.7)',
                statsDivider: 'rgba(255,255,255,0.25)',
                buttonBackground: 'rgba(255,255,255,0.15)',
                buttonText: '#FFFFFF',
                backButtonBg: 'rgba(255,255,255,0.1)',
                backButtonIcon: '#FFFFFF',
            };
        }
        // Modo claro
        return {
            background: '#F5F7FA',
            text: '#1F2937',
            textSecondary: '#6B7280',
            accent: HEADER_COLORS.accentPrimary,
            border: 'rgba(0,0,0,0.08)',
            statsBackground: 'rgba(91, 143, 249, 0.1)',
            statsText: '#1F2937',
            statsLabel: '#6B7280',
            statsDivider: 'rgba(0,0,0,0.15)',
            buttonBackground: HEADER_COLORS.accentPrimary,
            buttonText: '#FFFFFF',
            backButtonBg: 'rgba(0,0,0,0.06)',
            backButtonIcon: '#1F2937',
        };
    }, [isDarkMode]);

    // Estilos dinâmicos com Safe Area.
    // Quando recebe `gradient` (padrão de cabeçalho global da Home), alinha as
    // dimensões ao ScreenHeader das outras telas: mesmo paddingTop (insets+8),
    // mesmo raio (16) e paddingBottom (6), sem min/maxHeight (altura = conteúdo).
    const dynamicStyles = useMemo(() => {
        if (gradient || matchTabBar) {
            return {
                container: {
                    paddingTop: insets.top + 8,
                    paddingBottom: matchTabBar ? 10 : 6,
                    paddingHorizontal: spacing.lg,
                    // matchTabBar = mesma cor sólida + efeito 3D da BottomTabBar
                    // (sombra espelhada p/ baixo, borda e raio iguais aos da barra).
                    backgroundColor: matchTabBar
                        ? (isDarkMode ? '#151821' : '#FFFFFF')
                        : colors.background,
                    borderBottomLeftRadius: matchTabBar ? 20 : 16,
                    borderBottomRightRadius: matchTabBar ? 20 : 16,
                    ...(matchTabBar && {
                        borderBottomWidth: 1,
                        borderBottomColor: isDarkMode ? '#2A2D3A' : '#E5E7EB',
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 3 },
                        shadowOpacity: 0.15,
                        shadowRadius: 8,
                        elevation: 10,
                    }),
                },
            };
        }
        return {
            container: {
                paddingTop: insets.top + spacing.sm,
                paddingBottom: spacing.sm,
                paddingHorizontal: spacing.lg,
                backgroundColor: colors.background,
                minHeight: moderateScale(60) + insets.top,
                maxHeight: moderateScale(100) + insets.top,
            },
        };
    }, [insets.top, colors.background, gradient, matchTabBar, isDarkMode]);

    // Renderiza botão voltar
    const renderBackButton = () => {
        if (!showBackButton) return null;

        return (
            <TouchableOpacity
                style={[styles.backButton, { backgroundColor: colors.backButtonBg }]}
                onPress={onBackPress}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
                <Text style={[styles.backIcon, { color: colors.backButtonIcon }]}>←</Text>
            </TouchableOpacity>
        );
    };

    // Renderiza título e subtitle
    const renderTitle = () => {
        if (centerContent) return centerContent;

        return (
            <View style={styles.titleContainer}>
                {title && (
                    <Text
                        style={[
                            styles.title,
                            { color: colors.text },
                            isSmallScreen && styles.titleSmall
                        ]}
                        numberOfLines={1}
                    >
                        {title}
                    </Text>
                )}
                {badge && (
                    <TouchableOpacity
                        style={styles.badge}
                        onPress={onBadgePress}
                        activeOpacity={onBadgePress ? 0.75 : 1}
                        disabled={!onBadgePress}
                        accessibilityRole={onBadgePress ? 'button' : 'text'}
                    >
                        <Text style={styles.badgeText}>{badge}</Text>
                    </TouchableOpacity>
                )}
                {showStats && stats.questionCount !== undefined && (
                    <View style={[styles.statsCompact, { backgroundColor: colors.statsBackground }]}>
                        <View style={styles.statItem}>
                            <Text style={[styles.statNumber, { color: colors.statsText }]}>{stats.questionCount}</Text>
                            <Text style={[styles.statLabel, { color: colors.statsLabel }]}>questões</Text>
                        </View>
                        <View style={[styles.statDivider, { backgroundColor: colors.statsDivider }]} />
                        <View style={styles.statItem}>
                            <Text style={[styles.statNumber, { color: colors.statsText }]}>{stats.categoryCount}</Text>
                            <Text style={[styles.statLabel, { color: colors.statsLabel }]}>categorias</Text>
                        </View>
                    </View>
                )}
            </View>
        );
    };

    // Renderiza seção do usuário (direita)
    const renderUserSection = () => {
        if (rightContent) return rightContent;
        if (!showUserSection) return <View style={styles.rightPlaceholder} />;

        // Usuário não autenticado - botão "Entrar"
        // Mas se ainda está carregando, mostra placeholder para evitar flash
        if (!isAuthenticated) {
            // Se ainda está verificando a sessão, não mostra nada para evitar flash do botão "Entrar"
            if (authLoading) {
                return <View style={styles.rightPlaceholder} />;
            }

            return (
                <TouchableOpacity
                    style={[styles.enterButton, { backgroundColor: colors.buttonBackground }]}
                    onPress={onUserPress}
                >
                    <Text style={[styles.enterButtonText, { color: colors.buttonText }]}>Entrar</Text>
                </TouchableOpacity>
            );
        }

        // Usuário autenticado - mostrar avatar e stats
        return (
            <TouchableOpacity style={styles.userSection} onPress={onUserPress}>
                {/* Stats (ranking + XP) - apenas se fornecidos */}
                {(userStats.rank || userStats.xp !== undefined) && (
                    <View style={styles.userStatsColumn}>
                        {userStats.rank && (
                            <View style={styles.rankingBadge}>
                                <Text style={styles.rankingIcon}>🏆</Text>
                                <Text style={styles.rankingText}>#{userStats.rank}</Text>
                            </View>
                        )}
                        {userStats.xp !== undefined && (
                            <View style={styles.xpPill}>
                                <Text style={styles.xpIcon}>⭐</Text>
                                <Text style={styles.xpText}>
                                    {typeof userStats.xp === 'number'
                                        ? userStats.xp.toLocaleString()
                                        : userStats.xp}
                                </Text>
                            </View>
                        )}
                    </View>
                )}

                {/* Avatar */}
                {profile?.foto_url ? (
                    <Image
                        source={{ uri: profile.foto_url }}
                        style={styles.avatarImage}
                        contentFit="cover"
                        cachePolicy="memory-disk"
                    />
                ) : (
                    <View style={styles.avatarPlaceholder}>
                        <Text style={styles.avatarEmoji}>👤</Text>
                    </View>
                )}
            </TouchableOpacity>
        );
    };

    const inner = (
        <View style={styles.content}>
            {/* Lado esquerdo: Back button + Título */}
            <View style={styles.leftSection}>
                {renderBackButton()}
                {renderTitle()}
            </View>

            {/* Lado direito: User section */}
            <View style={styles.rightSection}>
                {renderUserSection()}
            </View>
        </View>
    );

    if (useGradient) {
        return (
            <LinearGradient
                colors={gradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.container, dynamicStyles.container, { backgroundColor: 'transparent' }]}
            >
                {inner}
            </LinearGradient>
        );
    }

    return (
        <View style={[styles.container, dynamicStyles.container]}>
            {inner}
        </View>
    );
});

Header.displayName = 'Header';

const styles = StyleSheet.create({
    container: {
        borderBottomLeftRadius: moderateScale(20),
        borderBottomRightRadius: moderateScale(20),
    },
    content: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    leftSection: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flex: 1,
    },
    rightSection: {
        minWidth: scaleWidth(100),
        alignItems: 'flex-end',
    },
    rightPlaceholder: {
        width: scaleWidth(50),
    },

    // Back Button
    backButton: {
        width: moderateScale(32),
        height: moderateScale(32),
        borderRadius: moderateScale(16),
        backgroundColor: 'rgba(255,255,255,0.1)',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: spacing.sm,
    },
    backIcon: {
        fontSize: fontSizes.lg,
        color: '#FFFFFF',
    },

    // Title
    titleContainer: {
        flexDirection: 'column',
        flex: 1,
    },
    title: {
        fontSize: fontSizes.xxl,
        fontWeight: '800',
        letterSpacing: -0.5,
    },
    titleSmall: {
        fontSize: fontSizes.xl,
    },
    badge: {
        backgroundColor: 'rgba(91, 143, 249, 0.15)',
        paddingHorizontal: spacing.sm,
        paddingVertical: moderateScale(3),
        borderRadius: moderateScale(10),
        alignSelf: 'flex-start',
        marginTop: spacing.xs,
    },
    badgeText: {
        fontSize: fontSizes.xs,
        fontWeight: '600',
        color: HEADER_COLORS.accentPrimary,
    },

    // Stats Compact
    statsCompact: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.12)',
        borderRadius: moderateScale(6),
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs,
        marginTop: spacing.xs,
        alignSelf: 'flex-start',
    },
    statItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    statNumber: {
        fontSize: fontSizes.sm,
        fontWeight: '700',
        color: '#FFFFFF',
        marginRight: moderateScale(3),
    },
    statLabel: {
        fontSize: fontSizes.xs,
        color: 'rgba(255,255,255,0.7)',
    },
    statDivider: {
        width: 1,
        height: moderateScale(12),
        backgroundColor: 'rgba(255,255,255,0.25)',
        marginHorizontal: spacing.md,
    },

    // Enter Button
    enterButton: {
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.sm,
        backgroundColor: 'rgba(255,255,255,0.15)',
        borderRadius: moderateScale(20),
    },
    enterButtonText: {
        color: '#FFFFFF',
        fontSize: fontSizes.md,
        fontWeight: '600',
    },

    // User Section
    userSection: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    userStatsColumn: {
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: spacing.sm,
    },
    rankingBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.4)',
        paddingHorizontal: spacing.sm,
        paddingVertical: moderateScale(3),
        borderRadius: moderateScale(12),
        marginRight: moderateScale(4),
    },
    rankingIcon: {
        fontSize: fontSizes.md,
        marginRight: moderateScale(3),
    },
    rankingText: {
        fontSize: fontSizes.sm,
        fontWeight: '700',
        color: HEADER_COLORS.accentGold,
    },
    xpPill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(139, 92, 246, 0.4)',
        paddingHorizontal: spacing.sm,
        paddingVertical: moderateScale(3),
        borderRadius: moderateScale(12),
    },
    xpIcon: {
        fontSize: fontSizes.xs,
        marginRight: moderateScale(3),
    },
    xpText: {
        fontSize: fontSizes.xs,
        fontWeight: '700',
        color: '#E9D5FF',
    },

    // Avatar
    avatarImage: {
        width: moderateScale(42),
        height: moderateScale(42),
        borderRadius: moderateScale(21),
        borderWidth: 2,
        borderColor: 'rgba(91, 143, 249, 0.5)',
    },
    avatarPlaceholder: {
        width: moderateScale(42),
        height: moderateScale(42),
        borderRadius: moderateScale(21),
        backgroundColor: 'rgba(91, 143, 249, 0.1)',
        borderWidth: 2,
        borderColor: HEADER_COLORS.accentPrimary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarEmoji: {
        fontSize: fontSizes.xl,
    },
});

export default Header;
